/**
 * HomeTab - Redesigned home dashboard with hero card and spending rules
 */
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import { 
  Plus, Edit3, Trash2, X, Save, GripVertical, Settings, Bell, 
  Wallet, TrendingUp, PiggyBank, Target, Calendar, ChevronRight,
  CreditCard, BarChart3, Lock, ChevronDown
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from "../ui/button";
import NotificationCenter from "../notifications/NotificationCenter";
import { Input } from "../ui/input";
import { formatCurrency } from "../../utils/formatters";
import { summarize as summarizePlans, nowLocalIso, todayLocalKey, findTransferSuggestions, buildReviewItems } from "../../utils/plansUtils";
import ProfilePicture from "../common/ProfilePicture";
import SpendingInsights from "../home/SpendingInsights";
import { countOverdue, dueNow } from "../../utils/reminderNotifications";
import { isRepeating, advanceDue } from "./NotesTab";
import CreditScoreCard from "../home/CreditScoreCard";
import CreditScoreSheet from "../home/CreditScoreSheet";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { PremiumFeature } from "../premium/PremiumComponents";
import { useTour } from "../../contexts/TourContext";
import { PLAN_TILE_PALETTES } from "../plans/plansShared";
import { usePurple } from "../../utils/purple";
import { usePlan, useMoneyPlanShown } from "../../utils/plan"; // plan_02, w2_01
import MoneyTimeline from "../plans/MoneyTimeline"; // plan_12
import { planSub, planStatus } from "../moneyPlan/MoneyPlan"; // mp_01
import { formatCurrencyWhole } from "../../utils/formatters";
import MonthEndPopup from "../review/MonthEndPopup";
import { YearReviewPopup } from "../review/YearReview"; // w2_18
import { MoneyClubCard, MoneyClubPage, useMoneyClubFeed, OPEN_MONEY_CLUB_EVENT, consumePendingMoneyClubOpen } from "../community/MoneyClub";

// ---- Rule of the day ----
// One rule is shown per day. The pick is remembered on the phone for the whole
// day, so reordering, adding or deleting mid-day never swaps it out. The next
// day moves on to the rule after it in the user's own order (not random, so
// every rule comes round once per cycle before any repeats).
const RULE_OF_DAY_KEY = 'worthly_rule_of_day';

const localDayKey = (d = new Date()) =>
  d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

// Whole days since a fixed local date, used to rotate the tint and to seed the
// first pick when nothing has been remembered yet.
const dayNumber = (d = new Date()) =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);

const pickRuleOfDay = (rules, today) => {
  if (!rules.length) return null;
  let stored = null;
  try { stored = JSON.parse(window.localStorage.getItem(RULE_OF_DAY_KEY) || 'null'); } catch (e) { stored = null; }
  if (stored && stored.date === today) {
    const same = rules.find((r) => r.id === stored.ruleId);
    if (same) return same;
  }
  if (stored && stored.ruleId) {
    const prev = rules.findIndex((r) => r.id === stored.ruleId);
    if (prev >= 0) return rules[(prev + 1) % rules.length];
  }
  return rules[dayNumber() % rules.length];
};

// SortableRuleItem component for drag-and-drop rules
const SortableRuleItem = ({ rule, index, editingId, editText, setEditText, startEditing, cancelEditing, saveEdit, removeRule, theme = 'pink', disabled = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingId === rule.id;
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // plan_03: Money plan rules - soft lilac numbers, calmer text.
  const planRule = usePlan();

  // Two-tone alternating colors for rule badges and left border, per theme —
  // reuses each theme's own established accent pair rather than a standalone palette
  const colors = isPink
    ? ['#e91e8c', '#A78BFA']
    : isWarm
      ? ['#2D5A3D', '#7C5C3A']
      : ['#4361EE', '#4338CA'];

  const colorIndex = index % colors.length;
  const badgeColor = colors[colorIndex];

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`flex items-center gap-2.5 py-3 bg-white transition-colors ${isDragging ? 'opacity-50' : ''}`}
      data-testid={`rule-item-${rule.id}`}
    >
      {/* Drag Handle */}
      <button 
        {...attributes} 
        {...listeners} 
        className="p-0.5 text-gray-300 hover:text-gray-400 cursor-grab active:cursor-grabbing"
        style={planRule ? { opacity: 0.55 } : undefined}
        data-testid={`rule-drag-${rule.id}`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      
      {/* Number Badge */}
      <div 
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-xs"
        style={planRule ? { backgroundColor: '#ECE6FA', color: '#7A5DD3', width: '26px', height: '26px', fontSize: '12.5px' } : { backgroundColor: badgeColor }}
      >
        {index + 1}
      </div>
      
      {/* Rule Text or Edit Input */}
      {isEditing ? (
        <div className="flex-1 flex gap-2">
          <Input
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && saveEdit(rule.id)}
            className="flex-1 text-xs"
            autoFocus
            data-testid={`rule-edit-input-${rule.id}`}
          />
          <Button onClick={() => saveEdit(rule.id)} size="sm" className="bg-gray-800 hover:bg-gray-900 text-white h-7 px-2" data-testid={`rule-save-${rule.id}`}>
            <Save className="w-3 h-3" />
          </Button>
          <Button onClick={cancelEditing} variant="ghost" size="sm" className="h-7 px-2" data-testid={`rule-cancel-${rule.id}`}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-between group">
          <p className="text-gray-800 text-xs font-medium leading-snug pr-2" style={planRule ? { fontSize: '14.5px', fontWeight: 400, lineHeight: 1.5, color: '#3E3C47' } : undefined}>{rule.text}</p>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button onClick={() => startEditing(rule)} className="p-1 text-gray-300 hover:text-gray-500 transition-colors" data-testid={`rule-edit-btn-${rule.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => removeRule(rule.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors" data-testid={`rule-delete-btn-${rule.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const HomeTab = ({ 
  user,
  userData,
  appSettings,
  rules = [], 
  saveUserData, 
  theme = 'pink',
  netWorth = 0,
  netWorthBreakdown = {},
  totalAssets = 0,
  totalSavings = 0,
  budgetLeftPercent = 0,
  onOpenSettings,
  onNavigateToTab,
  onProfileUpload,
  possessiveDisplayName = "Your",
  // New props for insights
  sinkingFunds = [],
  investmentAccounts = [],
  transactions = [],
  budgetSettings = {},
  categoryBudgets = {},
  noSpendDays = {},
  wishlistItems = [],
  previousNetWorth = 0,
  monthlyTargets = {},
  creditScore = {},
  timelineFunds = [], // plan_13
  onOpenMoneyPlan, // mp_01
  contributionsEnabled = true,
}) => {
  const [newRule, setNewRule] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [creditSheetOpen, setCreditSheetOpen] = useState(false);
  const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
  const [showAllRules, setShowAllRules] = useState(false);
  const [showRuleIdeas, setShowRuleIdeas] = useState(false);
  const [insightsDemoMode, setInsightsDemoMode] = useState(false);
  const [notifCenterOpen, setNotifCenterOpen] = useState(false);
  // Reminders ticked on Home: shown crossed out for a moment before they go.
  const [tickingIds, setTickingIds] = useState([]);
  // The last one ticked, so the Undo bar can put it back.
  const [undoReminder, setUndoReminder] = useState(null);
  // Money Club page, opened from the Home card or from Settings.
  const [clubOpen, setClubOpen] = useState(false);
  const clubFeed = useMoneyClubFeed(true);
  useEffect(() => {
    // Opened from a notification or Settings before Home was showing.
    if (consumePendingMoneyClubOpen()) setClubOpen(true);
    const openClub = () => { consumePendingMoneyClubOpen(); setClubOpen(true); };
    window.addEventListener(OPEN_MONEY_CLUB_EVENT, openClub);
    return () => window.removeEventListener(OPEN_MONEY_CLUB_EVENT, openClub);
  }, []);

  const { hasPremium, isAdmin, showFeaturePaywall, subscription, startTrial, loading } = useSubscription();
  const isPremiumUser = loading || hasPremium || isAdmin;

  // Scheduled contributions whose date has passed and need confirming -
  // surfaced as an insight now that the planner lives on Wealth > Invest.
  const plannerDueCount = (() => {
    if (!contributionsEnabled || !isPremiumUser) return 0;
    const p = userData?.plans || {};
    const accts = p.accounts || {};
    const sources = p.sources || [];
    const contributions = p.contributions || [];
    const taxYear = new Date().getFullYear();
    const todayKey = todayLocalKey();
    const dueOccurrences = [];
    ['rrsp', 'tfsa'].forEach((a) => {
      const s = summarizePlans({
        account: a, taxYear, sources, contributions,
        target: (accts[a] || {}).target || 0, roomAvailable: (accts[a] || {}).roomAvailable, todayKey,
      });
      (s.occurrences || []).forEach((o) => { if (o.state === 'due') dueOccurrences.push(o); });
    });
    // Same list the planner's "To review" card shows, so the numbers match.
    return buildReviewItems({ plans: p, transactions, taxYear, todayKey, dueOccurrences }).length;
  })();

  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only, on top of Pink): purple links, chips and buttons.
  const purple = usePurple();
  // plan_02: Money plan (admin-only) has its own calm top of Home.
  const plan = usePlan();
  const moneyPlanShown = useMoneyPlanShown(); // w2_01
  // mp_01: the money plan card's "on track" line, worked out like the plan does.
  const mpStatus = useMemo(() => (plan ? planStatus(userData?.money_diary?.plan, userData?.money_diary || {}, userData) : null), [plan, userData]);
  const PU = purple ? {
    link: '#A633C4', chip: '#F3E8FB', hover: 'hover:bg-[#F7F0FC]',
    btn: '#AC8DF2',
    shadow: '0 6px 22px rgba(120,60,150,0.09)',
  } : null;
  // Main button fill for the rule pop-ups.
  // plan_03: black buttons in the rule pop-ups.
  const ruleBtnBg = plan ? '#1F1E24' : PU ? PU.btn : isPink ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)' : isWarm ? '#5a9e78' : '#6381f0';

  // The tutorial's first step points at "Add a new rule", which only shows
  // when the list is open - so the list opens itself for that step.
  const { isTourActive, stepName } = useTour();
  const tourOnRules = isTourActive && stepName === 'rule';
  // Rule of the day only kicks in from 2 rules - with 0 or 1 the plain list
  // (and its Add button) stays, so nothing changes under a brand-new user.
  const rulesOpen = showAllRules || tourOnRules || rules.length < 2;

  const todayRuleKey = localDayKey();
  const ruleOfDay = pickRuleOfDay(rules, todayRuleKey);
  const ruleOfDayId = ruleOfDay ? ruleOfDay.id : null;
  useEffect(() => {
    if (!ruleOfDayId) return;
    try {
      window.localStorage.setItem(RULE_OF_DAY_KEY, JSON.stringify({ date: todayRuleKey, ruleId: ruleOfDayId }));
    } catch (e) {
      // Storage unavailable - the pick is still stable for this session.
    }
  }, [ruleOfDayId, todayRuleKey]);

  // Extract plain display name from possessive form (e.g., "Layemi's" -> "Layemi", "Your" -> "")
  const displayName = possessiveDisplayName === "Your" 
    ? "" 
    : possessiveDisplayName.replace(/'s$/i, '').replace(/s'$/i, 's');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Default spending rules ideas
  const defaultRules = [
    "Wait 24 hours before any non-essential purchase",
    "If I can't pay cash, I can't afford it",
    "One in, one out - donate something before buying",
    "No shopping when bored, tired, or emotional",
    "Unsubscribe from all promotional emails"
  ];

  const addRule = () => {
    if (newRule.trim()) {
      const rule = { id: String(Date.now()), text: newRule.trim(), created_at: new Date().toISOString() };
      saveUserData({ rules: [rule, ...rules] });
      setNewRule('');
    }
  };

  const addDefaultRule = (ruleText) => {
    const exists = rules.some(r => r.text.toLowerCase() === ruleText.toLowerCase());
    if (!exists) {
      const rule = { id: String(Date.now()), text: ruleText, created_at: new Date().toISOString() };
      saveUserData({ rules: [rule, ...rules] });
    } else {
      customToast.error("Already exists", "This rule is already in your list");
    }
  };

  const removeRule = (id) => {
    saveUserData({ rules: rules.filter(r => r.id !== id) });
  };

  const startEditing = (rule) => {
    setEditingId(rule.id);
    setEditText(rule.text);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = (id) => {
    if (editText.trim()) {
      const updatedRules = rules.map(r => r.id === id ? { ...r, text: editText.trim() } : r);
      saveUserData({ rules: updatedRules });
    }
    cancelEditing();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = rules.findIndex(r => r.id === active.id);
      const newIndex = rules.findIndex(r => r.id === over.id);
      const newRules = arrayMove(rules, oldIndex, newIndex);
      saveUserData({ rules: newRules });
    }
  };

  // Hero gradient styles based on theme
  const accentRGB = isPink ? '233,30,140' : isWarm ? '124,92,58' : '99,102,241';
  const accentDeep = isPink ? '#B0166B' : isWarm ? '#9A4628' : '#3730a3';
  const heroStyle = {
    background: `linear-gradient(140deg, rgba(${accentRGB},0.06) 0%, rgba(${accentRGB},0.0) 60%), #ffffff`,
    boxShadow: `0 1px 3px rgba(${accentRGB},0.06), 0 4px 16px rgba(${accentRGB},0.08)`,
    border: `1px solid rgba(${accentRGB},0.08)`,
  };

  // Quick nav shortcuts with theme-specific background colors
  // Returns inline style for tile icon background
  // Colours mirror BudgetHero's tilePalette exactly, so Home and Budget read
  // as one system: Budget=Income, Wealth=Leftover, Calendar=Spent, Wishlist=Saved/Inv.
  // (Run patch_budgethero_gradient_tiles.py on BudgetHero.jsx too, so the
  // blue/warm gradients below match what's actually there.)
  const getShortcutIconStyle = (shortcutId) => {
    const tiles = {
      pink: {
        budget:   { background: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)' },
        wealth:   { background: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)' },
        calendar: { background: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)' },
        wishlist: { background: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)' },
      },

      blue: {
        budget:   { background: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)' },
        wealth:   { background: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)' },
        calendar: { background: 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)' },
        wishlist: { background: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)' },
      },

      warm: {
        budget:   { background: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)' },
        wealth:   { background: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)' },
        calendar: { background: 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)' },
        wishlist: { background: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)' },
      }
    };
    const themeKey = isWarm ? 'warm' : isPink ? 'pink' : 'blue';
    return tiles[themeKey][shortcutId] || { background: '#f3f4f6' };
  };

  const getShortcutCardStyle = (id) => (({
    background: '#ffffff'
  }));

  const shortcuts = [
    { id: 'budget', label: 'Budget', icon: '💳', tab: 'budget' },
    { id: 'wealth', label: 'Wealth', icon: '💎', tab: 'savings' },
    { id: 'calendar', label: 'Calendar', icon: '📅', tab: 'calendar' },
    { id: 'wishlist', label: 'Wishlist', icon: '🛍️', tab: 'wishlist' },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* plan_02: Money plan top of Home - greeting, net worth, credit + Money Club rows */}
      {plan ? (() => {
        const now = new Date();
        const hour = now.getHours();
        const hello = hour < 12 ? 'good morning' : hour < 17 ? 'good afternoon' : 'good evening';
        const dateLabel = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
        const card = { background: '#FFFFFF', borderRadius: '24px', boxShadow: '0 1px 2px rgba(60,40,120,0.03)' };
        // w2_12: smaller and quieter, so they don't compete with the white cards
        const iconBtn = { width: '34px', height: '34px', borderRadius: '14px', background: 'rgba(122,93,211,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' };
        const soft = '#8B879A';
        const ink = '#26252E';

        const diff = (previousNetWorth !== undefined && previousNetWorth !== null) ? netWorth - previousNetWorth : 0;

        const csOn = creditScore?.enabled !== false;
        const csEntries = [...(creditScore?.entries || [])].sort((x, y) => x.month_key.localeCompare(y.month_key));
        const csLatest = csEntries.length ? csEntries[csEntries.length - 1].score : null;

        const clubList = Array.isArray(clubFeed?.feed) ? clubFeed.feed : (clubFeed?.feed?.posts || []);
        const clubLatest = clubList && clubList.length ? clubList[0] : null;
        const clubTitle = (clubLatest && (clubLatest.title || clubLatest.headline)) || 'Reads and wins from the Worthly team';

        // plan_06: the Money Club row follows the Settings switch.
        const clubOnHome = (userData?.money_club || {}).show_on_home !== false;

        // plan_11: cleaner top - date under the greeting, one card for net
        // worth, credit score and Money Club.
        const nwShort = (() => {
          const sym = (formatCurrencyWhole(0) || '$').replace(/[\d.,\s-]/g, '') || '$';
          const a = Math.abs(netWorth || 0);
          const sign = (netWorth || 0) < 0 ? '-' : '';
          const short = a >= 1e6 ? (a / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M'
            : a >= 1e4 ? (a / 1e3).toFixed(1).replace(/\.0$/, '') + 'k'
            : null;
          return short ? sign + sym + short : formatCurrencyWhole(netWorth);
        })();
        const tile = { display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '22px', padding: '12px 13px', border: 'none', textAlign: 'left', minWidth: 0, width: '100%' };
        return (
          <div data-testid="plan-home-top">
            <div className="flex items-start justify-between px-1" style={{ gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <h1 data-testid="home-title" style={{ margin: 0 }}>{hello}{displayName ? ', ' + displayName : ''}!</h1>
                <p style={{ margin: '5px 0 0', fontSize: '14px', color: soft }}>{dateLabel}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setNotifCenterOpen(true)} style={{ ...iconBtn, borderRadius: '999px' }} data-testid="notif-btn">
                  <Bell style={{ width: 15, height: 15, color: '#6E6A7C' }} />
                </button>
                <button onClick={onOpenSettings} style={{ ...iconBtn, borderRadius: '999px' }} data-testid="settings-btn">
                  <Settings style={{ width: 15, height: 15, color: '#6E6A7C' }} />
                </button>
              </div>
            </div>

            {/* w2_15: net worth under the greeting, like the Pink layout */}
            <button
              type="button"
              onClick={() => onNavigateToTab && onNavigateToTab('savings')}
              className="block text-left"
              style={{ background: 'none', border: 0, padding: '0 4px', marginTop: '22px' }}
              data-testid="home-net-worth-block"
            >
              <span style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: soft }}>Net worth</span>
              <span data-testid="home-net-worth" style={{ display: 'block', fontSize: '38px', fontWeight: 600, letterSpacing: '-1.4px', lineHeight: 1.1, color: '#7A5DD3', marginTop: '2px' }}>{nwShort}</span>
              {diff !== 0 && (
                <span className="inline-flex items-center" style={{ gap: '6px', marginTop: '10px', background: diff > 0 ? '#DFF2E7' : '#ECE6FA', color: diff > 0 ? '#2F8756' : '#7A5DD3', borderRadius: '999px', padding: '6px 12px', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
                    <path d={diff > 0 ? 'M3 13 L13 3 M6 3 H13 V10' : 'M3 3 L13 13 M6 13 H13 V6'} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {diff > 0 ? '+' : '\u2212'}{formatCurrencyWhole(Math.abs(diff))} this month
                </span>
              )}
            </button>

            {/* plan_12: swipe between your money timeline and every payday. w2_15: net worth moved above.
                w2_21: hidden from Settings > Home */}
            {!userData?.hide_money_timeline && (
            <MoneyTimeline
              style={{ marginTop: '22px' }}
              timeline={userData?.money_timeline}
              notes={(userData?.plans?.notes) || []}
              funds={timelineFunds}
              onSave={(next) => saveUserData({ money_timeline: next })}
              onOpenNote={(n) => {
                try { localStorage.setItem('worthly_open_note', n.id); } catch (e) { /* private mode */ }
                if (onNavigateToTab) onNavigateToTab('plannotes');
              }}
            />
            )}
            {(csOn || clubOnHome) && (
              <div className="grid" style={{ gridTemplateColumns: csOn && clubOnHome ? '1fr 1fr' : '1fr', gap: '10px', marginTop: '14px', marginBottom: '22px' }}>
                {csOn && (
                  <button type="button" onClick={() => setCreditSheetOpen(true)} style={{ ...tile, background: '#FFFFFF' }} data-testid="credit-score-card">
                    <span style={{ width: '36px', height: '36px', borderRadius: '999px', background: '#DFE9F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>{'⭐'}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '12px', color: '#55525F' }}>Credit score</span>
                      <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {csLatest === null ? 'Start tracking' : String(csLatest)}{/* plan_15: just the score */}
                      </span>
                    </span>
                  </button>
                )}
                {clubOnHome && (
                  <button type="button" onClick={() => setClubOpen(true)} style={{ ...tile, background: '#FFFFFF' }} data-testid="plan-money-club-row" title={clubTitle}>
                    <span style={{ width: '36px', height: '36px', borderRadius: '999px', background: '#ECE6FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>{'\u{1F48C}'}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '12px', color: '#55525F' }}>Money Club</span>
                      <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clubLatest ? 'Latest post' : 'See posts'}</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })() : (
      <>
      {/* Purple: name, net worth and shortcuts sit in the gradient band (wy-ph). */}
      <div className="wy-ph" style={{ '--ph-lift': '1.5rem', '--ph-pad': '1.25rem' }}>
      {/* Name + icons row */}
      <div className="mb-10 px-1">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="flex-shrink-0">
              <ProfilePicture
                user={user}
                userData={userData}
                appSettings={appSettings}
                onUpload={onProfileUpload}
                theme={theme}
                size="sm"
                showBorder={false}
              />
            </div>
            <h1
              style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px', lineHeight: 1.1 }}
              data-testid="home-title"
            >
              {possessiveDisplayName} Finances
            </h1>
          </div>
          {/* Bell + Settings */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setNotifCenterOpen(true)}
              className={`w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-sm`}
              data-testid="notif-btn"
            >
              <Bell className={`w-4 h-4 ${purple ? 'text-[#A633C4]' : isWarm ? 'text-[#2D5A3D]' : isPink ? 'text-pink-500' : 'text-indigo-500'}`} />
            </button>
            <button
              onClick={onOpenSettings}
              className={`w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-sm`}
              data-testid="settings-btn"
            >
              <Settings className={`w-4 h-4 ${purple ? 'text-[#A633C4]' : isWarm ? 'text-[#2D5A3D]' : isPink ? 'text-pink-500' : 'text-indigo-500'}`} />
            </button>
          </div>
        </div>
      </div>

      <div>
      <div className="mb-2 px-1">
        <p className="text-[13px] font-bold tracking-[0.08em] uppercase" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Net worth</p>
      </div>

      {/* Big net worth number */}
      <p
        className="hero-gradient-text"
        style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: '40px',
          fontWeight: 400,
          letterSpacing: '-1.5px',
          lineHeight: 1,
          marginBottom: '8px',
        }}
        data-testid="home-net-worth"
      >
        {netWorth >= 1000000 ? (netWorth/1000000).toFixed(1).replace(/\.0$/,'')+'M' : netWorth >= 1000 ? (netWorth/1000).toFixed(1).replace(/\.0$/,'')+'k' : formatCurrency(netWorth)}
      </p>

      {/* Trend badge */}
      {previousNetWorth !== undefined && previousNetWorth !== null && previousNetWorth !== netWorth && (
        <div style={{ marginBottom: '40px' }}>
          {(() => {
            const diff = netWorth - previousNetWorth;
            const isPositive = diff >= 0;
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '12px', fontWeight: 700,
                color: isPositive ? '#2E9E7B' : '#c87878',
                background: isPositive ? '#E8F5F0' : 'rgba(200,120,120,0.1)',
                padding: '4px 10px', borderRadius: '8px',
              }}>
                <>{isPositive ? '↑' : '↓'} {isPositive ? '+' : ''}{formatCurrency(diff)} this month</>
              </span>
            );
          })()}
        </div>
      )}
      </div>

      {/* Quick Navigation Shortcuts */}
      <div className="grid grid-cols-4 gap-2 mb-10">
        {shortcuts.map((shortcut) => (
          <button
            key={shortcut.id}
            onClick={() => onNavigateToTab?.(shortcut.tab)}
            className="rounded-2xl p-2.5 shadow-sm flex flex-col items-center gap-1.5 hover:shadow-md transition-shadow wy-shortcut"
            style={getShortcutCardStyle(shortcut.id)}
            data-testid={`shortcut-${shortcut.id}`}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={getShortcutIconStyle(shortcut.id)}>
              {shortcut.icon}
            </div>
            <span className="text-[10px] font-medium text-gray-700">{shortcut.label}</span>
          </button>
        ))}
      </div>
      </div>

      </>
      )}

      {/* Reminders overdue or due today. A list you can tick off from Home;
          sits above the insights rail because a missed bill can't be skimmed. */}
      {(() => {
        const allReminders = (userData?.plans?.reminders) || [];
        const due = dueNow(allReminders);
        // Free users can't open Reminders, so this would be a dead end.
        if (!isPremiumUser || (due.length === 0 && !undoReminder)) return null;
        const todayK = todayLocalKey();
        const shortDate = (d) => {
          const [y, m, dd] = d.split('-').map(Number);
          return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1] + ' ' + dd;
        };
        const timeLabel = (iso) => {
          const t = String(iso).slice(11, 16);
          if (!t) return '';
          const [h, mm] = t.split(':').map(Number);
          return ((h % 12) || 12) + ':' + String(mm).padStart(2, '0') + (h < 12 ? ' AM' : ' PM');
        };
        const eyebrowC = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
        const todayChip = isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
        const todayInk = isPink ? '#6B55A3' : isWarm ? '#2D5A3D' : '#4361EE';
        const shown = due.slice(0, 3);

        const tick = (r) => {
          if (tickingIds.includes(r.id)) return;
          setTickingIds((ids) => [...ids, r.id]);
          window.setTimeout(() => {
            const nowIso = nowLocalIso();
            const next = allReminders.map((x) => {
              if (x.id !== r.id) return x;
              // Same as ticking on the Reminders page: a repeat moves on to
              // its next date rather than finishing.
              if (isRepeating(x)) {
                const nd = advanceDue(x.dueAt, x.repeat, nowIso, x.repeatDay);
                if (nd) return { ...x, dueAt: nd, done: false, doneAt: null, lastDoneAt: todayK };
              }
              return { ...x, done: true, doneAt: todayK };
            });
            saveUserData({ plans: { ...(userData?.plans || {}), reminders: next } });
            setTickingIds((ids) => ids.filter((i) => i !== r.id));
            setUndoReminder(r);
            window.setTimeout(() => setUndoReminder((u) => (u && u.id === r.id ? null : u)), 5000);
          }, 650);
        };

        const undo = () => {
          const r = undoReminder;
          if (!r) return;
          const next = allReminders.map((x) => (x.id === r.id ? r : x));
          saveUserData({ plans: { ...(userData?.plans || {}), reminders: next } });
          setUndoReminder(null);
        };

        const openReminders = () => onNavigateToTab?.('planreminders');

        // plan_15: Money plan look - date tiles like the Reminders page.
        if (plan) {
          const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const tileParts = (r, late) => {
            const t = String(r.dueAt).slice(11, 16);
            if (!late && t) {
              const [h, mm] = t.split(':').map(Number);
              return { big: ((h % 12) || 12) + (mm ? ':' + String(mm).padStart(2, '0') : ''), small: h < 12 ? 'am' : 'pm' };
            }
            const k = r.dueAt.slice(0, 10);
            return { big: String(Number(k.slice(8, 10))), small: MON[Number(k.slice(5, 7)) - 1] };
          };
          return (
            <div style={{ marginBottom: 24 }} data-testid="home-reminders">
              {due.length > 0 && (
                <>
                  <div className="flex items-center" style={{ gap: 8, margin: '0 4px 10px' }}>
                    <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>Reminders {'\u{1F514}'}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: '#FAE4ED', color: '#C04B7C' }}>{due.length}</span>
                  </div>
                  <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '4px 18px' }}>
                    {shown.map((r, idx) => {
                      const late = r.dueAt.slice(0, 10) < todayK;
                      const ticking = tickingIds.includes(r.id);
                      const tp = tileParts(r, late);
                      return (
                        <div
                          key={r.id}
                          className="flex items-center"
                          style={{
                            gap: 13, padding: '12px 0',
                            borderTop: idx === 0 ? 'none' : '1px solid #EEEAF6',
                            transition: 'opacity .35s ease, transform .35s ease',
                            opacity: ticking ? 0.55 : 1,
                            transform: ticking ? 'translateX(8px)' : 'none',
                          }}
                        >
                          <button type="button" onClick={openReminders} className="flex items-center flex-1 min-w-0 text-left" style={{ gap: 13, background: 'none', border: 0, padding: 0 }}>
                            <span className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: late ? '#FAE4ED' : '#ECE6FA', color: late ? '#C04B7C' : '#7A5DD3', lineHeight: 1 }}>
                              <b style={{ fontSize: tp.big.length > 2 ? 14 : 17, fontWeight: 600 }}>{tp.big}</b>
                              <small style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', marginTop: 3, textTransform: 'uppercase' }}>{tp.small}</small>
                            </span>
                            <span className="flex-1 min-w-0">
                              <span style={{ display: 'block', fontSize: 15, fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: ticking ? '#B8B2B6' : '#33343A', textDecoration: ticking ? 'line-through' : 'none' }}>
                                {r.emoji ? r.emoji + ' ' : ''}{r.title || 'Reminder'}{/* plan_16 */}
                              </span>
                              <span style={{ display: 'block', fontSize: 12.5, marginTop: 3, color: late ? '#C04B7C' : '#8B879A' }}>
                                {late ? 'Overdue · was due ' + shortDate(r.dueAt.slice(0, 10)) : 'Due today'}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => tick(r)}
                            aria-label="Mark done"
                            className="flex items-center justify-center flex-shrink-0"
                            style={{ width: 26, height: 26, borderRadius: 999, padding: 0, color: '#FFFFFF', fontSize: 13, fontWeight: 600, border: '2px solid ' + (ticking ? '#9ED9B6' : late ? '#F2AFC8' : '#DCD3F4'), background: ticking ? '#9ED9B6' : 'transparent' }}
                          >
                            {ticking ? '✓' : ''}
                          </button>
                        </div>
                      );
                    })}
                    {due.length > 3 && (
                      <button type="button" onClick={openReminders} className="w-full text-center" style={{ padding: '12px 0 10px', borderTop: '1px solid #EEEAF6', fontSize: 13.5, fontWeight: 600, color: '#7A5DD3', background: 'none' }}>
                        See all {due.length} {'›'}
                      </button>
                    )}
                  </div>
                </>
              )}
              {undoReminder && (
                <div className="flex items-center justify-between" style={{ marginTop: 10, borderRadius: 999, padding: '11px 18px', background: '#1F1E24', color: '#FFFFFF', fontSize: 13.5 }}>
                  <span>Marked done</span>
                  <button type="button" onClick={undo} style={{ fontWeight: 600, color: '#B9A6F0', background: 'none', border: 0 }}>Undo</button>
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="mb-6" data-testid="home-reminders">
            {due.length > 0 && (
              <>
                <div className="flex items-center justify-between px-1 mb-2">
                  <p className="text-[13px] font-bold tracking-[0.08em] uppercase m-0" style={{ color: eyebrowC }}><span className="wy-star">✦ </span>Reminders</p>
                  <span
                    className="flex items-center justify-center text-[12px] font-extrabold text-white"
                    style={{ background: '#E11D48', minWidth: '22px', height: '22px', borderRadius: '999px', padding: '0 7px' }}
                  >
                    {due.length}
                  </span>
                </div>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {shown.map((r, idx) => {
                    const late = r.dueAt.slice(0, 10) < todayK;
                    const ticking = tickingIds.includes(r.id);
                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-3 px-3.5 py-3"
                        style={{
                          borderTop: idx === 0 ? 'none' : '1px solid #F3EDF1',
                          transition: 'opacity .35s ease, transform .35s ease',
                          opacity: ticking ? 0.55 : 1,
                          transform: ticking ? 'translateX(8px)' : 'none',
                        }}
                      >
                        <button type="button" onClick={openReminders} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <span
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                            style={{ background: late ? '#FDE7EC' : todayChip, opacity: ticking ? 0.5 : 1 }}
                          >
                            {late ? '🚨' : '🔔'}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span
                              className="block text-[14.5px] font-bold truncate"
                              style={{ color: ticking ? '#B8B2B6' : '#1F2937', textDecoration: ticking ? 'line-through' : 'none' }}
                            >
                              {r.title || 'Reminder'}
                            </span>
                            <span className="flex items-center gap-1.5 mt-1 text-[12px]" style={{ color: '#8C8789' }}>
                              <span
                                className="text-[10.5px] font-bold rounded-full px-2 py-0.5 flex-shrink-0"
                                style={late ? { background: '#FDE7EC', color: '#BE123C' } : { background: todayChip, color: todayInk }}
                              >
                                {late ? 'Overdue' : 'Due today'}
                              </span>
                              <span className="truncate">{late ? 'was due ' + shortDate(r.dueAt.slice(0, 10)) : timeLabel(r.dueAt)}</span>
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => tick(r)}
                          aria-label="Mark done"
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[14px] font-extrabold"
                          style={ticking
                            ? { background: '#2F9E6B', border: '2px solid #2F9E6B' }
                            : { border: '2px solid ' + (late ? '#F5A3B5' : '#D9CCE3') }}
                        >
                          {ticking ? '✓' : ''}
                        </button>
                      </div>
                    );
                  })}
                  {due.length > 3 && (
                    <button
                      type="button"
                      onClick={openReminders}
                      className="w-full text-left px-3.5 py-2.5 text-[12.5px] font-semibold"
                      style={{ borderTop: '1px solid #F3EDF1', color: PU ? PU.link : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE' }}
                    >
                      See all {due.length} {'›'}
                    </button>
                  )}
                </div>
              </>
            )}
            {undoReminder && (
              <div className="mt-2.5 rounded-xl px-3.5 py-2.5 flex items-center justify-between text-[12.5px]" style={{ background: '#1F1B24', color: '#fff' }}>
                <span>Marked done</span>
                <button type="button" onClick={undo} className="font-bold" style={{ color: '#F5A8C1' }}>Undo</button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Money Club: newest post from the Worthly team */}
      {!plan && <MoneyClubCard feed={clubFeed.feed} userData={userData} theme={theme} onOpen={() => setClubOpen(true)} />}

      {/* Spending Insights */}
      <div className="mb-3">
        <SpendingInsights
          theme={theme}
          sinkingFunds={sinkingFunds}
          investmentAccounts={investmentAccounts}
          transactions={transactions}
          budgetSettings={budgetSettings}
          categoryBudgets={categoryBudgets}
          noSpendDays={noSpendDays}
          wishlistItems={wishlistItems}
          netWorth={netWorth}
          previousNetWorth={previousNetWorth}
          netWorthBreakdown={netWorthBreakdown}
          monthlyTargets={monthlyTargets}
          demoMode={insightsDemoMode}
          showDemoToggle={isAdmin}
          onToggleDemoMode={() => setInsightsDemoMode(!insightsDemoMode)}
          maxInsights={!isPremiumUser ? 1 : undefined}
          lockedTeasers={!isPremiumUser ? [
            {
              type: 'savingsGoal',
              category: 'SAVINGS',
              title: 'Savings goal complete!',
              description: 'You hit your $500 savings goal this month!',
              value: '$500',
              badge: { text: '100% ✓', isPositive: true },
              progress: 100,
              progressLabel: 'Goal complete!',
              progressPercent: '🎉',
              icon: 'party'
            },
            {
              type: 'netWorthUp',
              category: 'NET WORTH',
              title: 'Net worth up +$1,250!',
              description: 'From $23,500 → $24,750. Great progress!',
              value: '$24,750',
              badge: { text: '↑ +$1,250', isPositive: true },
              icon: 'chartUp'
            },
            {
              type: 'investment',
              category: 'INVESTMENTS',
              title: 'RRSP at 45% of goal',
              description: '$9,000 toward $20,000 target.',
              value: '$9,000',
              badge: { text: '45%', isPositive: false },
              progress: 45,
              progressLabel: 'of $20,000',
              icon: 'satellite'
            }
          ] : []}
          onUnlockClick={!isPremiumUser ? () => showFeaturePaywall('insights') : undefined}
          onAction={(tab) => onNavigateToTab?.(tab)}
          leadingInsights={plannerDueCount > 0 ? [{
            type: 'plannerDue',
            category: 'INVESTMENTS',
            title: `${plannerDueCount} contribution${plannerDueCount === 1 ? '' : 's'} to review`,
            description: 'Scheduled contributions to your TFSA or RRSP and budget transfers have passed. Confirm what went in so your planner stays accurate.',
            value: String(plannerDueCount),
            badge: { text: 'to review', isPositive: false },
            icon: 'clipboard',
            action: 'plans',
          }] : []}
        />
      </div>

      {/* Spending Rules - Rule of the day, expands to the full list */}
      {(() => {
        const eyebrow = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
        const linkCls = PU
          ? `text-[#A633C4] ${PU.hover}`
          : isWarm
          ? 'text-[#2D5A3D] hover:bg-[#F3E5D1]/50'
          : isPink
            ? 'text-pink-500 hover:bg-pink-50'
            : 'text-indigo-500 hover:bg-indigo-50';
        // One fixed colour per theme: the same one the Contributions Planner
        // row below uses for its confirmed amount, so the two links agree.
        const seeAllColor = PU ? PU.link : isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE';
        const ideasLeft = defaultRules.filter(rule => !rules.some(r => r.text.toLowerCase() === rule.toLowerCase())).length > 0;

        // plan_06: Money plan rules - one card, heading and + Add inside, one
        // rule until See all, See less folds back to one.
        if (plan) {
          const one = ruleOfDay || rules[0] || null;
          const oneIdx = one ? rules.findIndex((r) => r.id === one.id) : -1;
          const num = (n) => (
            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#ECE6FA', color: '#7A5DD3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12.5px', fontWeight: 600, flexShrink: 0 }}>{n}</span>
          );
          const linkStyle = { fontSize: '14px', fontWeight: 600, color: '#8B879A', background: 'none', border: 0, padding: 0 };
          return (
            <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '22px 20px 18px', marginTop: '16px' }} data-testid="plan-rules-card">
              <div className="flex items-baseline justify-between" style={{ gap: '12px', marginBottom: '6px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.6px', color: '#26252E' }}>Your spending rules</span>
                <button onClick={() => setShowAddRuleDialog(true)} style={linkStyle} data-testid="add-rule-btn" data-tour-target="addRuleBtn">+ Add</button>
              </div>
              {rules.length === 0 ? (
                <p className="m-0" style={{ fontSize: '14.5px', lineHeight: 1.5, color: '#8B879A', padding: '8px 0 4px' }}>
                  No rules yet. Tap + Add to write your first one!
                </p>
              ) : !rulesOpen && one ? (
                <button onClick={() => setShowAllRules(true)} className="w-full flex text-left" style={{ gap: '12px', alignItems: 'flex-start', padding: '11px 0', background: 'none', border: 0 }} data-testid="rule-of-the-day">
                  {num(oneIdx + 1)}
                  <span style={{ flex: 1, fontSize: '14.5px', lineHeight: 1.5, color: '#3E3C47' }}>{one.text}</span>
                </button>
              ) : (
                <div className="divide-y divide-gray-100 wy-plan-rules">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={rules.map(r => r.id)} strategy={verticalListSortingStrategy}>
                      {rules.map((rule, index) => (
                        <SortableRuleItem
                          key={rule.id}
                          rule={rule}
                          index={index}
                          editingId={editingId}
                          editText={editText}
                          setEditText={setEditText}
                          startEditing={startEditing}
                          cancelEditing={cancelEditing}
                          saveEdit={saveEdit}
                          removeRule={removeRule}
                          theme={theme}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}
              {(rules.length >= 2 && !tourOnRules) || (rulesOpen && ideasLeft) ? (
                <div className="flex items-center justify-center" style={{ gap: '22px', paddingTop: '10px' }}>
                  {rules.length >= 2 && !tourOnRules && (
                    <button onClick={() => setShowAllRules(!showAllRules)} className="flex items-center" style={{ ...linkStyle, gap: '4px' }} data-testid="rules-see-all-btn">
                      {showAllRules ? 'See less' : `See all ${rules.length}`}
                      <ChevronDown className={'w-4 h-4' + (showAllRules ? ' rotate-180' : '')} />
                    </button>
                  )}
                  {rulesOpen && ideasLeft && (
                    <button onClick={() => setShowRuleIdeas(true)} style={linkStyle} data-testid="need-ideas-btn">{'✨'} Need ideas?</button>
                  )}
                </div>
              ) : null}
            </div>
          );
        }

        return (
          <>
            <div className="mt-3 mb-2 px-1">
              <p className="text-[13px] font-bold tracking-[0.08em] uppercase" style={{ color: eyebrow }}>
                <span className="wy-star">✦ </span>{''}{!rulesOpen && ruleOfDay ? 'Rule of the day' : 'Your spending rules'}
              </p>
            </div>

            {!rulesOpen && ruleOfDay ? (
              <div
                className="bg-white rounded-2xl shadow-sm mb-1"
                style={{ padding: '16px 16px 4px' }}
                data-testid="rule-of-the-day"
              >
                <p className="m-0 text-[15px]" style={{ color: '#1F2937', fontWeight: 400, lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
                  {ruleOfDay.text}
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAllRules(true)}
                    className="flex items-center gap-1 text-[13px] font-semibold"
                    style={{ color: seeAllColor, height: '40px', paddingLeft: '12px' }}
                    data-testid="rules-see-all-btn"
                  >
                    {`See all ${rules.length}`}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {rules.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No rules yet. Add your first spending rule!</p>
                  </div>
                ) : (
                  <div className="px-4 divide-y divide-gray-100">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={rules.map(r => r.id)} strategy={verticalListSortingStrategy}>
                        {rules.map((rule, index) => (
                          <SortableRuleItem
                            key={rule.id}
                            rule={rule}
                            index={index}
                            editingId={editingId}
                            editText={editText}
                            setEditText={setEditText}
                            startEditing={startEditing}
                            cancelEditing={cancelEditing}
                            saveEdit={saveEdit}
                            removeRule={removeRule}
                            theme={theme}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {rules.length >= 2 && !tourOnRules && (
                  <button
                    onClick={() => setShowAllRules(false)}
                    className={`w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-gray-100 transition-colors ${linkCls}`}
                    data-testid="rules-toggle-btn"
                  >
                    <span className="font-medium text-xs">Back to rule of the day</span>
                    <ChevronDown className="w-3.5 h-3.5 rotate-180" />
                  </button>
                )}

                {/* Add Rule + Need Ideas Buttons */}
                <div className="flex items-center border-t border-gray-100">
                  <button
                    onClick={() => setShowAddRuleDialog(true)}
                    className={`flex-1 p-3 flex items-center justify-center gap-1.5 transition-colors ${linkCls}`}
                    data-testid="add-rule-btn"
                    data-tour-target="addRuleBtn"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium text-xs">Add a new rule</span>
                  </button>
                  {ideasLeft && (
                    <button
                      onClick={() => setShowRuleIdeas(true)}
                      className={`flex-1 p-3 flex items-center justify-center gap-1 transition-colors border-l border-gray-100 ${linkCls}`}
                      data-testid="need-ideas-btn"
                    >
                      <span className="text-xs">✨</span>
                      <span className="font-medium text-xs">Need ideas?</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* w2_24: your money plan, between your spending rules and Your plans (yours only, hidden by the Developer switch) */}
      {plan && (
      <>
      {isAdmin && moneyPlanShown && userData?.money_diary?.plan && (
      <button
        type="button"
        onClick={() => onOpenMoneyPlan && onOpenMoneyPlan()}
        className="w-full text-left"
        style={{ display: 'block', marginTop: '14px', background: '#FFFFFF', borderRadius: '28px', padding: '20px 18px 18px', border: 0 }}
        data-testid="home-money-plan"
      >
        <span className="flex items-start justify-between" style={{ gap: '12px' }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '19px', fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>Your money plan {'\u{1F5FA}️'}</span>
            <span style={{ display: 'block', margin: '5px 0 0', fontSize: '13.5px', color: '#7A7A82', lineHeight: 1.45 }}>{planSub(userData?.money_diary?.plan)}</span>
          </span>
          <span style={{ width: '50px', height: '50px', borderRadius: '999px', background: '#DFF2E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '23px', flexShrink: 0 }}>{mpStatus ? mpStatus.emoji : '\u{1F9ED}'}</span>
        </span>
        <span className="flex items-center" style={{ gap: '12px', background: '#F4F1FB', borderRadius: '20px', padding: '14px 16px', marginTop: '16px' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {mpStatus ? (
              <>
                <span style={{ display: 'block', fontSize: '12.5px', color: '#9A8FBF', fontWeight: 500 }}>{mpStatus.onTrack ? "You're on track ✨" : "Let's catch up \u{1F4AA}\u{1F3FE}"}</span>
                <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: '#2B2C31', marginTop: '3px', lineHeight: 1.3 }}>{mpStatus.line}</span>
              </>
            ) : (
              <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: '#2B2C31', lineHeight: 1.3 }}>Your long-term plan, all in one place!</span>
            )}
          </span>
          <span style={{ flexShrink: 0, background: '#1F1E24', color: '#FFFFFF', borderRadius: '999px', padding: '10px 16px', fontSize: '13.5px', fontWeight: 600 }}>Open</span>
        </span>
      </button>
      )}
      </>
      )}

      {/* Financial Plans */}
      {(() => {
        const plansData = userData?.plans || {};
        const planAccounts = plansData.accounts || {};
        const planSources = plansData.sources || [];
        const planContributions = plansData.contributions || [];
        const rrspTarget = (planAccounts.rrsp || {}).target || 0;
        const tfsaTarget = (planAccounts.tfsa || {}).target || 0;
        const hasPlans = planSources.length > 0 || rrspTarget > 0 || tfsaTarget > 0;

        const planYear = new Date().getFullYear();
        const eyebrowColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
        const planAccent = isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE';
        const planAlt = isPink ? '#9B7FD0' : isWarm ? '#8B6B4D' : '#a855f7';
        const planTrack = PU ? PU.chip : isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';

        let confirmed = 0;
        let targetTotal = 0;
        let dueCount = 0;

        if (hasPlans) {
          const todayKey = todayLocalKey();
          ['rrsp', 'tfsa'].forEach((acct) => {
            const s = summarizePlans({
              account: acct,
              taxYear: planYear,
              sources: planSources,
              contributions: planContributions,
              target: (planAccounts[acct] || {}).target || 0,
              roomAvailable: (planAccounts[acct] || {}).roomAvailable,
              todayKey,
            });
            confirmed += s.confirmed;
            targetTotal += s.target;
            (s.perSource || []).forEach((p) => { dueCount += p.dueCount || 0; });
          });
        }

        const noteCount = (plansData.notes || []).filter((n) => !n.archived
          && (n.year == null || n.year === planYear)).length;
        const budgetSheetCount = (plansData.budgetSheets || []).length;

        // Same overdue rule the Reminders list uses: a whole day has to have
        // passed, not just the hour.
        const nowIso = nowLocalIso();
        const todayIso = nowIso.slice(0, 10);
        // Every year, matching the Reminders tab - a reminder is a point in
        // time rather than something that belongs to a tax year.
        const yearReminders = (plansData.reminders || []).filter((r) => r.dueAt);
        const reminderCount = yearReminders.filter((r) => !r.done).length;
        const overdueCount = yearReminders.filter((r) => !r.done
          && r.dueAt < nowIso && r.dueAt.slice(0, 10) !== todayIso).length;

        const pinnedCount = (plansData.notes || []).filter((n) => !n.archived && n.kind === 'pinned'
          && (n.year == null || n.year === planYear)).length;
        const snapshotCount = (userData?.future_worth?.snapshots || []).length;
        const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 's');

        // Free users see every card, readable, with one lock on the section
        // header. Any tap opens the same paywall, which lists all of these.
        const locked = !isPremiumUser;
        const open = (route) => (locked ? showFeaturePaywall('notesReminders') : onNavigateToTab?.(route));

        const cardShadow = PU ? PU.shadow : '0 1px 3px rgba(0,0,0,0.04)';
        const chip = (emoji) => (
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ background: planTrack }}>
            {emoji}
          </div>
        );
        const titleStyle = { color: '#1F2937', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 };

        const tiles = [
          {
            id: 'reminders', short: 'Reminders', emoji: '⏰', title: 'Reminders', route: 'planreminders',
            lines: locked ? ['Get nudged before bills and deadlines']
              : reminderCount > 0 ? [plural(reminderCount, 'reminder')] : ['Nothing coming up'],
          },
          {
            id: 'notes', short: 'Notes', emoji: '📝', title: 'Notes', route: 'plannotes',
            lines: locked ? ['Keep money notes by year']
              : noteCount > 0 ? [plural(noteCount, 'note'), pinnedCount > 0 ? pinnedCount + ' pinned' : null] : ['No notes yet'],
          },
          {
            id: 'budgets', short: 'Sheets', emoji: '🧾', title: 'Budget Sheets', route: 'planbudgets',
            lines: locked ? ['Plan one-off budgets']
              : budgetSheetCount > 0 ? [plural(budgetSheetCount, 'sheet')] : ['Plan a one-off budget'],
          },
          {
            id: 'futureworth', short: 'Future', emoji: '🔮', title: 'Future Worth', route: 'futureworth',
            lines: locked ? ["See where you'll be in a few years"]
              : snapshotCount > 0 ? [plural(snapshotCount, 'snapshot')] : ['See where a plan could take you'],
          },
        ];

        const pct = targetTotal > 0 ? Math.min(100, (confirmed / targetTotal) * 100) : 0;

        // plan_06: Money plan - one white card, four soft tiles.
        if (plan) {
          const PT = {
            reminders: ['🔔', 'Reminders'],
            notes: ['📝', 'Notes'],
            budgets: ['📋', 'Budget sheets'],
            futureworth: ['🔮', 'Future Worth'],
            yearlycosts: ['📆', 'Yearly costs'], // w2_16
            debtpayoff: ['🏁', 'Debt payoff'], // w2_17
          };
          return (
            <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '22px 20px', marginTop: '16px' }} data-testid="plan-plans-card">
              <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.6px', color: '#26252E' }}>Your plans</span>
                {locked && (
                  <button onClick={() => showFeaturePaywall('notesReminders')} style={{ fontSize: '12.5px', fontWeight: 600, color: '#6C6879', background: '#F5F2FA', borderRadius: '999px', padding: '5px 11px', border: 0 }} data-testid="home-plans-premium-pill">
                    {'🔒'} Premium
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2" style={{ gap: '9px' }}>
                {[...tiles, { id: 'yearlycosts', route: 'yearlycosts' }, { id: 'debtpayoff', route: 'debtpayoff' }].map((t) => ( /* w2_16, w2_17 */
                  <button key={t.id} onClick={() => open(t.route)} style={{ position: 'relative', background: '#F7F5FB', borderRadius: '18px', padding: '16px 6px', textAlign: 'center', border: 0 }} data-testid={'home-plan-tile-' + t.id}>
                    <span style={{ display: 'block', fontSize: '22px', lineHeight: 1, marginBottom: '8px' }}>{PT[t.id] ? PT[t.id][0] : t.emoji}</span>
                    <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#5E5A6B' }}>{PT[t.id] ? PT[t.id][1] : t.title}</span>
                    {t.id === 'reminders' && !locked && overdueCount > 0 && (
                      <span aria-label="Overdue" style={{ position: 'absolute', top: '12px', right: '14px', width: '8px', height: '8px', borderRadius: '50%', background: '#E58AAE' }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        }

        return (
          <>
            <div className="mt-3 mb-2 px-1 flex items-center justify-between">
              <p className="text-[13px] font-bold tracking-[0.08em] uppercase m-0" style={{ color: eyebrowColor }}><span className="wy-star">✦ </span>{''}Your plans</p>
              {locked && (
                <button
                  onClick={() => showFeaturePaywall('notesReminders')}
                  className="text-[12px] font-bold rounded-full"
                  style={{ background: '#fff', color: eyebrowColor, padding: '4px 11px', boxShadow: cardShadow }}
                  data-testid="home-plans-premium-pill"
                >
                  {'🔒'} Premium
                </button>
              )}
            </div>

            {/* Contributions Planner now lives on Wealth > Invest. */}
            <div className="grid grid-cols-2 gap-2.5 mb-1">
              {tiles.map((t) => (
                <button
                  key={t.id}
                  onClick={() => open(t.route)}
                  className="bg-white rounded-2xl p-3.5 text-left flex flex-col"
                  style={{ boxShadow: cardShadow, minHeight: '124px' }}
                  data-testid={'home-plan-tile-' + t.id}
                >
                  {chip(t.emoji)}
                  <p className="m-0 mt-3 text-[15px]" style={titleStyle}>{t.title}</p>
                  {t.lines.filter(Boolean).map((line, i) => (
                    <p key={i} className="m-0 mt-0.5 text-[12px] text-gray-500 leading-snug">{line}</p>
                  ))}
                </button>
              ))}
            </div>
          </>
        );
      })()}

      {/* Credit Health Section */}
      {!plan && (creditScore?.enabled !== false) && (
        <>
          <div className="mt-4 mb-2 px-1">
            <p className="text-[13px] font-bold tracking-[0.08em] uppercase" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Credit health</p>
          </div>
          <CreditScoreCard
            creditScore={creditScore}
            theme={theme}
            onClick={() => setCreditSheetOpen(true)}
          />
        </>
      )}

      {/* Buying tools moved to the top of the Wishlist tab. */}

      {/* Rule Ideas Popup */}
      {showRuleIdeas && (() => {
        const availableRules = defaultRules.filter(rule => 
          !rules.some(r => r.text.toLowerCase() === rule.toLowerCase())
        );
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={() => setShowRuleIdeas(false)}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div 
              className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-lg font-normal text-black" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  Spending Rule Ideas
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Tap a rule to add it</p>
              </div>

              {/* Rules List */}
              <div className="px-5 pb-3 space-y-2">
                {availableRules.map((rule, index) => (
                  <button 
                    key={index} 
                    onClick={() => {
                      addDefaultRule(rule);
                    }} 
                    className="w-full text-left p-3 bg-white rounded-full border border-gray-100 hover:shadow-md transition-all flex items-center gap-3"
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: ruleBtnBg,
                      }}
                    >
                      {/* w2_11: a drawn + so it sits in the middle */}
                      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1.5v9M1.5 6h9" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" /></svg>
                    </div>
                    <span className="flex-1 text-gray-700 text-sm font-medium leading-snug">{rule}</span>
                  </button>
                ))}
              </div>

              {/* Done Button */}
              <div className="px-5 pb-5 pt-2">
                <button
                  onClick={() => setShowRuleIdeas(false)}
                  className="w-full py-3 rounded-full text-sm font-bold text-white transition-opacity hover:opacity-90"
                  style={{
                    background: ruleBtnBg,
                  }}
                  data-testid="rule-ideas-done"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Rule Dialog */}
      {/* Add Rule Popup */}
      {showAddRuleDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={() => setShowAddRuleDialog(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div 
            className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-lg font-normal text-black" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Add Spending Rule
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">Create a rule to guide your spending</p>
            </div>

            {/* Input */}
            <div className="px-5 pb-3">
              <Input
                type="text"
                placeholder="Write a spending rule that motivates you..."
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && newRule.trim() && (addRule(), setShowAddRuleDialog(false))}
                className="bg-gray-50 border-gray-200"
                data-testid="rule-input"
                autoFocus
              />
            </div>

            {/* Buttons */}
            <div className="px-5 pb-5 pt-2 flex gap-2">
              <button
                onClick={() => setShowAddRuleDialog(false)}
                className="flex-1 py-3 rounded-full text-sm font-bold text-gray-500 bg-white border border-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { addRule(); setShowAddRuleDialog(false); }}
                disabled={!newRule.trim()}
                className="flex-1 py-3 rounded-full text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{
                  background: ruleBtnBg,
                }}
                data-testid="add-rule-submit"
              >
                Add Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Score Sheet */}
      <CreditScoreSheet
        isOpen={creditSheetOpen}
        onClose={() => setCreditSheetOpen(false)}
        creditScore={creditScore}
        saveUserData={saveUserData}
        theme={theme}
      />

      {/* Month-end review pop-up (1st-7th of the month, plus a one-time launch pop-up) */}
      <MonthEndPopup
        userData={userData}
        saveUserData={saveUserData}
        transactions={transactions}
        noSpendDays={noSpendDays}
        sinkingFunds={sinkingFunds}
        categoryBudgets={categoryBudgets}
        theme={theme}
        suppressed={isTourActive}
      />

      {/* w2_18: year in review pop-up (Worthly 2.0) */}
      {plan && <YearReviewPopup userData={userData} saveUserData={saveUserData} suppressed={isTourActive} />}

      {/* Money Club page */}
      {clubOpen && (
        <MoneyClubPage
          feed={clubFeed.feed}
          setFeed={clubFeed.setFeed}
          reload={clubFeed.reload}
          userData={userData}
          saveUserData={saveUserData}
          theme={theme}
          firstName={displayName}
          onClose={() => setClubOpen(false)}
        />
      )}

      {/* Notification Center */}
      <NotificationCenter
        open={notifCenterOpen}
        onOpenChange={setNotifCenterOpen}
        theme={theme}
      />
    </div>
  );
};

export default HomeTab;
